import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {UtilsService} from "../utils.service";

@Component({
  selector: 'app-image-preview-actions',
  templateUrl: './image-preview-actions.component.html',
  styleUrls: ['./image-preview-actions.component.scss']
})
export class ImagePreviewActionsComponent implements OnChanges {
  @Input() imageUrl!: string; // required
  @Input() alt: string = 'Image';
  @Input() downloadFileBaseName: string | null = null;
  @Input() imageClass: string = 'img-fluid';
  @Input() maxHeight: string = '400px';

  imageName: string = 'image';

  constructor(
    public utils: UtilsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.imageUrl || changes.downloadFileBaseName) {
      this.imageName = this.getDownloadFileName();
    }
  }

  /**
   * Returns the download file name based on the URL file name and custom base name.
   * If a custom base name is provided, it will be used; otherwise, the URL file name will be used.
   * If the URL file name has an extension, it will be preserved; otherwise, the custom base name will be used as-is.
   *
   * @return {string} The download file name to be used when saving the file.
   */
  private getDownloadFileName(): string {
    const urlFileName = this.getFileNameFromUrl(this.imageUrl);
    const urlExtension = this.getFileExtension(urlFileName);

    const baseName = this.downloadFileBaseName?.trim();

    // No custom name: use the URL file name as-is.
    if (!baseName) {
      return urlFileName || 'image';
    }

    const baseNameWithoutExtension = this.removeFileExtension(baseName);

    // If URL has extension, always use URL extension.
    if (urlExtension) {
      return `${baseNameWithoutExtension}.${urlExtension}`;
    }

    // If URL has no extension, use custom name as-is.
    return baseName;
  }

  /**
   * Returns the file name from a given URL.
   *
   * @param {string} url - The URL from which to extract the file name.
   * @return {string} - The extracted file name from the URL. Returns 'image' if no file name is found.
   */
  private getFileNameFromUrl(url: string): string {
    try {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const fileName = cleanUrl.split('/').pop();

      return fileName || 'image';
    } catch {
      return 'image';
    }
  }

  /**
   * Retrieves the file extension from the given file name.
   *
   * @param {string} fileName - The name of the file including extension.
   * @return {string} The file extension in lowercase. An empty string is returned if no valid extension is found.
   */
  private getFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
      return '';
    }

    return fileName.substring(lastDotIndex + 1).toLowerCase();
  }

  /**
   * Removes the file extension from a given file name
   *
   * @param {string} fileName - The name of the file including its extension
   * @return {string} The file name without the file extension
   */
  private removeFileExtension(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return fileName;
    }

    return fileName.substring(0, lastDotIndex);
  }

  /**
   * Opens the provided URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void}
   */
  onOpenUrl(url: string): void {
    if ( !url ) {
      return;
    }
    this.utils.openUrl(url);
  }

  /**
   * Displays a toast message informing the user that the URL has been copied to the clipboard.
   *
   * @return {void}
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }
}
